<script>
   /*************************************************************/
   /* PLSModelCalculator                                        */
   /* -------------------------                                 */
   /* component for training a PLS model                        */
   /*************************************************************/

   import { Switch, Container, Button, Spinner, ButtonCancel, Range } from 'svelte-controls-basic';
   import { app_pls_predict, genhash, relabelObjects, withoutExcluded } from '@mdatools-apps/shared/utils';
   import { pls_fit } from '@mdatools-apps/wasm/pls.js';


   let {
      WASM,                      // promise with WASM methods
      dataset,                   // object with training set
      relabeled = [],            // array with dataset row numbers (from 0) marked as relabeled
      outliers = [],             // array with dataset row numbers (from 0) marked as outliers
      exclvars = [],             // array with predictor numbers (from 0) markred as excluded
      resobj = $bindable(),      // object with PLS results
      model = $bindable()        // object with PLS model
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   // error handling parameters
   let status = $state('neutral');
   let errMsg = $state('');

   // model calculation parameters — maximum number of components the dataset allows, and the
   // default choice. ncomp is derived so it follows the dataset size, but stays writable so the
   // Range can override it; the override is dropped when maxNComp changes, as before
   let maxNComp = $derived(dataset ? Math.min(dataset.values.nrows - 2 - outliers.length,
      dataset.values.ncols - dataset.nresp - exclvars.length - 1, 50) : null);

   // the class sizes the fit would see: the dummy column with the relabels applied and the
   // outliers left out. a class of one object has no deviation (in 'getParams' of
   // 'PLSDASettings' the classic 's' divides by n - 1 and is NaN, the robust one is the MAD of a
   // single value, zero) and one of none no estimate at all; the loader admits such a class as
   // loaded, and the outliers and the relabels can make one of a balanced set, so the check sits
   // where every fit passes
   let smallClass = $derived.by(() => {
      if (!dataset) return null;
      const y = dataset.values.getcolref(1);
      const n = [0, 0];
      for (let i = 0; i < dataset.values.nrows; i++) {
         if (outliers.includes(i)) continue;
         n[(y[i] > 0) !== relabeled.includes(i) ? 0 : 1]++;
      }
      const c = n.findIndex(v => v < 2);
      return c < 0 ? null : {name: dataset.classNames[c], n: n[c]};
   });

   // a model needs room for at least one component and each term above can take that away: two
   // objects go to the estimate itself, every excluded object takes one more, and a single
   // predictor leaves nothing to decompose; a short class (above) refuses the fit on its own.
   // without this the button was offered on a set 'pls_fit' refuses, with a slider clamped to
   // one step or none at all, and the reason came only after the click
   let canFit = $derived(maxNComp !== null && maxNComp >= 1 && smallClass === null);
   let ncomp = $derived(canFit ? Math.min(maxNComp, 20) : null);

   // says which of the terms ran out, or which class is short, so the message can name the thing
   // the user can change. the predictor branch is live: the dataset here is the preprocessed one,
   // and a trim step can cut it down to fewer than two predictors
   let tooSmallMsg = $derived(
      !dataset || canFit ? '' :
      dataset.values.ncols - dataset.nresp - exclvars.length < 2 ?
         'A model needs at least two predictors.' :
      maxNComp < 1 ? (outliers.length > 0 ?
         'Too many objects are excluded — ' + (dataset.values.nrows - outliers.length) + ' of ' +
         dataset.values.nrows + ' are left, and a model needs at least three.' :
         'A model needs at least three objects.') :
      'Class "' + smallClass.name + '" has ' + smallClass.n + ' object' +
         (smallClass.n === 1 ? '' : 's') + ' and needs at least two.'
   );
   let center = $state(true);
   let scale = $state(false);

   // promise for model calculation method
   let promise = $state();

   // every asynchronous write is stamped with the run which asked for it, and only the newest
   // run may write; a run is retired by a newer one and by the component going away. the C call
   // itself blocks the page, so the window is the module instantiation, which yields: a cancel
   // of the data or of the preprocessing pressed in it unmounts the component, and the model
   // still landed in the session through the bindings, for a set that was gone - the next set
   // loaded then found it there, fitted. plain 'let', never '$state' (see 'PrepWidget')
   let run = 0;
   $effect(() => () => { run++; });

   /**
    * Fit PLS model and apply it to training set.
    * @param {Object} dataset - object with training set.
    * @param {Number} ncomp - number of PCs in the model.
    * @param {boolean} center - mean center data columns or not.
    * @param {boolean} scale - standardize data columns or not.
    *
    * Method does not return anything but assigns values to the global parameters "model" and "res".
    */
   async function computeModel(dataset, ncomp, center, scale) {
      const nresp = dataset.nresp;
      const varindices = dataset.varindices;
      const myRun = ++run;

      promise = WASM()
         .then(Module => {
            if (myRun !== run) return;

            // change the y-reference values of the objects which have been relabeled. this sits
            // INSIDE the chain deliberately: 'compute' calls this async function without
            // awaiting it, so a throw raised out here would be an unhandled rejection - the
            // assignment to 'promise' below would never happen, the controls would stay hidden
            // behind 'status !== "computing"', the spinner behind '{#if promise}', and the user
            // would face a blank panel with the error only in the console. Raised in here it
            // reaches the '.catch' at the end of this chain, which sets 'status' to 'error'
            const values = relabelObjects(dataset.values.copy(), relabeled, nresp);

            // fit model and add extra parameters
            const m = pls_fit(Module, values, nresp, ncomp, center, scale, outliers, exclvars);

            m['hash'] = genhash();                          // unique hash number to trace model updates

            // here we remove labels, values and indices for excluded predictors
            m['varlabels'] = withoutExcluded(dataset.varlabels, exclvars, varindices, nresp);
            m['varindices'] = withoutExcluded(dataset.varindices, exclvars, varindices, nresp);
            m['varvalues'] = withoutExcluded(dataset.varvalues, exclvars, varindices, nresp);

            // these ones needed for plots
            m['xvarlabels'] = m['varlabels'].filter((v, i) => (i + 1) > nresp);
            m['xvarvalues'] = m['varvalues'].filter((v, i) => (i + 1) > nresp);
            m['xvarindices'] = m['varindices'].filter((v, i) => (i + 1) > nresp).map(v => v + 1);

            m['varvaluesName'] = dataset.varvaluesName;    // title for variable axis
            m['varvaluesUnits'] = dataset.varvaluesUnits;  // units if variales are represented by numeric values, e.g. nm
            m['varrev'] = dataset.varrev                   // if variables must be reversed
            m['prep'] = null;                              // array with preprocessing methods will be added when model is downloaded

            // make predictions without preprocessing model
            // as training data is already preprocessed
            const r = app_pls_predict(Module, m, dataset, outliers, false, relabeled);

            // add explained variance to model
            m['stat'] = {
               xexpvar: r.stat.xexpvar,
               yexpvar: r.stat.yexpvar,
            }

            // set global variables and status of the component. 'neutral', which used to be
            // 'success': nothing told the two apart, and the write only has to leave 'computing',
            // which hides the button once the model is cleared with the component still mounted
            model = m;
            resobj = r;
            status = 'neutral';
         })
         .catch(e =>{
            // something went wrong
            console.error(e);
            if (myRun !== run) return;
            model = null;
            status = 'error';
            // the message only: the box printed the Error object, which reads 'Error: ...'
            errMsg = e instanceof Error ? e.message : String(e);
         });
   }

   /**
    * Event handler for computing the model
    * @param e — event object.
    */
   function compute() {
      status = 'computing';
      errMsg = null;
      computeModel(dataset, ncomp, center, scale);
   }

   /**
    * Resets the component status.
    */
   function reset() {
      status = 'neutral';
      promise = null;
      resobj = null;
      model = null;
      errMsg = null;
   }
</script>

{#if (dataset && !model) && status !== 'computing'}
   {#if !canFit}
   <Container name="too-small" status="error">
      <p>{tooSmallMsg}</p>
   </Container>
   {:else}
   <!-- settings for global model calculation -->
   <Container name="ncomp" label="# of LVs" id="{uid}-ncomp">
      <Range bind:value={ncomp} min={1} max={maxNComp} step={1} decNum={0} />
   </Container>
   <Container name="center" label="Center" id="{uid}-center">
      <Switch bind:value={center} options={["no", "yes"]} />
   </Container>
   <Container name="scale" label="Scale" id="{uid}-scale">
      <Switch bind:value={scale} options={["no", "yes"]} />
   </Container>
   <Container name="create-model" label=" ">
      <Button onclick={compute} text="Create model" />
   </Container>
   {/if}
{/if}


{#if promise}

   <!-- calculation is in process. the chain above ends in a catch of its own which reports
        through 'errMsg', so the promise never rejects and a catch block here would never show -->
   {#await promise}
      <Container name="spinner">
         <Spinner />
         <p>Computing global model...</p>
      </Container>
   {/await}

{/if}

{#if model && model.ncomp }
<Container name="info">
   <p><span style="color:#a0a0a0;">PLS model:</span> {model.ncomp} LVs{model.center ? ', centered' : ''}{model.scale ? ', scaled' : ''}</p>
   <ButtonCancel onclick={reset} />
</Container>
{/if}


<!-- error message -->
{#if status === 'error' || errMsg}
<Container name="error" status="error">
   <p>{errMsg}</p>
</Container>
{/if}

